import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

const ReportInterpretation = ({ interpretation, userType = "individual", compact = false }) => {
  const { t, language } = useLanguage();
  
  if (!interpretation) {
    return null;
  }

  const getIconForSection = (section) => {
    switch (section) {
      case 'profile_summary':
        return '👤';
      case 'strengths_analysis':
        return '💪';
      case 'needs_analysis':
        return '🎯';
      case 'development_areas':
        return '📈';
      case 'compatibility_insight':
        return '🤝';
      case 'balance_insight':
        return '⚖️';
      case 'growth_potential':
        return '🚀';
      default:
        return '📊';
    }
  };

  const sectionTitles = {
    profile_summary: { es: 'Resumen del Perfil', en: 'Profile Summary' },
    strengths_analysis: { es: 'Fortalezas Clave', en: 'Key Strengths' },
    needs_analysis: { es: 'Necesidades Principales', en: 'Primary Needs' },
    development_areas: { es: 'Áreas de Desarrollo', en: 'Development Opportunities' },
    compatibility_insight: { es: 'Análisis de Compatibilidad', en: 'Compatibility Analysis' },
    balance_insight: { es: 'Evaluación de Balance', en: 'Balance Assessment' },
    growth_potential: { es: 'Potencial de Crecimiento', en: 'Growth Potential' }
  };

  const getSectionTitle = (section) => {
    return sectionTitles[section]?.[language] || sectionTitles[section]?.en || section.replace('_', ' ');
  };

  const sections = [
    'profile_summary',
    'strengths_analysis', 
    'needs_analysis',
    'development_areas',
    'compatibility_insight',
    'balance_insight',
    'growth_potential'
  ];

  // Compact mode - simpler, more condensed layout
  if (compact) {
    return (
      <div className="space-y-3">
        {sections.map((section) => {
          if (!interpretation[section]) return null;
          
          return (
            <div key={section} className="border-l-2 border-gray-200 pl-3">
              <h5 className="font-medium text-sm text-gray-800 flex items-center gap-1 mb-1">
                <span className="text-sm">{getIconForSection(section)}</span>
                {getSectionTitle(section)}
              </h5>
              <p className="text-sm text-gray-600 leading-relaxed">
                {interpretation[section]}
              </p>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-4 mt-6">
      <Card className="border-l-4 border-l-blue-500">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            📋 {language === 'es' ? 'Análisis Profesional e Insights' : 'Professional Analysis & Insights'}
          </CardTitle>
          <CardDescription>
            {language === 'es' ? 'Interpretación del perfil de dimensiones asistida por IA' : 'AI-powered interpretation of your Dimensions profile'}
          </CardDescription>
        </CardHeader>
      </Card>

      <div className="grid gap-4">
        {sections.map((section) => {
          if (!interpretation[section]) return null;
          
          return (
            <Card key={section} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <span className="text-xl">{getIconForSection(section)}</span>
                  {getSectionTitle(section)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  {interpretation[section]}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

// Component for comparison interpretations
export const ComparisonInterpretation = ({ interpretation, comparisonType }) => {
  if (!interpretation) {
    return null;
  }

  const getComparisonIcon = (type) => {
    switch (type) {
      case 'employee_vs_company':
        return '🏢👥';
      case 'candidate_vs_company':
        return '🏢🎯';
      case 'employee_vs_candidate':
        return '👥🎯';
      default:
        return '🔄';
    }
  };

  const sections = [
    'comparison_summary',
    'affinities',
    'complements', 
    'potential_challenges',
    'collaboration_opportunities'
  ];

  const getSectionTitle = (section) => {
    switch (section) {
      case 'comparison_summary':
        return 'Comparison Overview';
      case 'affinities':
        return 'Shared Strengths';
      case 'complements':
        return 'Complementary Areas';
      case 'potential_challenges':
        return 'Potential Challenges';
      case 'collaboration_opportunities':
        return 'Collaboration Opportunities';
      default:
        return section.replace('_', ' ');
    }
  };

  const getSectionIcon = (section) => {
    switch (section) {
      case 'comparison_summary':
        return '📊';
      case 'affinities':
        return '🤝';
      case 'complements':
        return '🔄';
      case 'potential_challenges':
        return '⚠️';
      case 'collaboration_opportunities':
        return '💡';
      default:
        return '📝';
    }
  };

  return (
    <div className="space-y-4 mt-6">
      <Card className="border-l-4 border-l-green-500">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {getComparisonIcon(comparisonType)} Comparison Analysis
          </CardTitle>
          <CardDescription>
            Detailed analysis of compatibility and collaboration potential
          </CardDescription>
        </CardHeader>
      </Card>

      <div className="grid gap-4">
        {sections.map((section) => {
          if (!interpretation[section]) return null;
          
          return (
            <Card key={section} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <span className="text-xl">{getSectionIcon(section)}</span>
                  {getSectionTitle(section)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  {interpretation[section]}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default ReportInterpretation;